/*
  Announcement bar — van reveal.

  Drives a van across the utility bar on every message change: the outgoing message is wiped
  away at the van's nose and the incoming one is uncovered behind its tail. Pairs with
  assets/component-announcement-van-custom.css and the `van_reveal` branch of
  sections/announcement-bar.liquid, which is where the markup and the settings live.

  Why this and not Dawn's slideshow-component: the wipe needs the two messages STACKED and
  clipped against a moving boundary, and Dawn's slider scrolls one slide out while the next
  scrolls in. There is no boundary to clip against in that model. Everything else the slider
  gave us here — autoplay, the dwell setting, the aria-live region — is reproduced below; the
  prev/next buttons are not, because a two-message trust bar has nothing to page through.

  Positions are written to CSS custom properties from a rAF loop rather than animated in CSS.
  The van and the two clip boundaries have to agree to the pixel every frame, and driving them
  from one number is the only way to guarantee that; animating a custom property in CSS would
  need @property registration and would still leave three independent timelines to keep in
  step.
*/
(() => {
  if (customElements.get('van-announcement-bar')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VanAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.vanbar__track');
      this.van = this.querySelector('.vanbar__van');
      this.messages = Array.from(this.querySelectorAll('.vanbar__msg'));

      // One message is a static bar, not a slideshow. Leave it exactly as rendered.
      if (!this.track || !this.van || this.messages.length < 2) return;

      this.index = Math.max(
        0,
        this.messages.findIndex((el) => el.classList.contains('is-current'))
      );
      this.autoplay = this.dataset.autoplay === 'true';
      this.dwell = (parseFloat(this.dataset.speed) || 5) * 1000;
      this.driveMs = (parseFloat(this.dataset.drive) || 1.4) * 1000;

      /*
        Anything that should stop the bar rotating takes out a named hold; the timer only runs
        when every hold has been released. A counter would drift — 'hidden' and 'offscreen'
        can both be taken and released in either order — and a single boolean would let
        whichever released last restart the bar while another reason still stood.
      */
      this.holds = new Set();

      this.onPointerEnter = () => this.hold('hover');
      this.onPointerLeave = () => this.release('hover');
      this.onFocusIn = () => this.hold('focus');
      this.onFocusOut = () => this.release('focus');
      this.onVisibility = () => (document.hidden ? this.hold('hidden') : this.release('hidden'));

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);

      // The bar is at the top of the page, so this is mostly about a customer who has scrolled
      // away: no reason to burn a rAF loop on a van nobody can see.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) =>
            entry.isIntersecting ? this.release('offscreen') : this.hold('offscreen')
          );
        });
        this.observer.observe(this);
      }

      if (window.Shopify && window.Shopify.designMode) this.bindThemeEditor();
      if (document.hidden) this.holds.add('hidden');

      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.raf);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.observer) this.observer.disconnect();
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    /*
      Theme editor: selecting an announcement in the sidebar must show that announcement and
      hold it there, or David is editing text he cannot see. The event target is the block
      element itself — the one carrying block.shopify_attributes, i.e. our .vanbar__msg.
    */
    bindThemeEditor() {
      this.onBlockSelect = (event) => {
        const selected = this.messages.indexOf(event.target);
        if (selected === -1) return;
        this.hold('editor');
        this.show(selected);
      };
      this.onBlockDeselect = (event) => {
        if (this.messages.indexOf(event.target) === -1) return;
        this.release('editor');
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    hold(reason) {
      this.holds.add(reason);
      clearTimeout(this.timer);
    }

    release(reason) {
      this.holds.delete(reason);
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.holds.size || this.driving) return;
      this.timer = setTimeout(() => this.drive(), this.dwell);
    }

    drive() {
      const width = this.track.clientWidth;
      const vanWidth = this.van.offsetWidth || 36;
      const next = (this.index + 1) % this.messages.length;

      // No width means the bar is not laid out (display:none in a preview, say). A drive in
      // that state would set nonsense offsets, so swap and carry on.
      if (!width || REDUCED_MOTION.matches) {
        this.show(next);
        this.schedule();
        return;
      }

      const incoming = this.messages[next];
      incoming.classList.add('is-incoming');
      incoming.removeAttribute('aria-hidden');
      this.messages[this.index].setAttribute('aria-hidden', 'true');
      this.classList.add('is-driving');
      this.driving = true;

      // Tail runs from just off the left edge to the right edge; the nose is one van ahead of
      // it, so by the end of the run both boundaries have cleared the bar completely.
      const distance = width + vanWidth;
      const started = performance.now();

      const frame = (now) => {
        const p = Math.min(1, (now - started) / this.driveMs);
        // easeInOutQuad — pulls away and slows to a stop rather than crossing at a constant
        // speed. Anything sharper reads as a wipe with a van drawn on it.
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        const tail = -vanWidth + eased * distance;

        this.style.setProperty('--van-tail', `${tail}px`);
        this.style.setProperty('--van-lead', `${Math.max(0, tail + vanWidth)}px`);
        this.style.setProperty('--van-bob', `${Math.sin(p * Math.PI * 12) * 0.5}px`);

        if (p < 1) {
          this.raf = requestAnimationFrame(frame);
          return;
        }

        this.driving = false;
        this.style.removeProperty('--van-tail');
        this.style.removeProperty('--van-lead');
        this.style.removeProperty('--van-bob');
        this.show(next);
        this.schedule();
      };

      this.raf = requestAnimationFrame(frame);
    }

    /*
      Settle on one message with no animation — the end of a drive, a reduced-motion swap, or a
      block selected in the theme editor.
    */
    show(index) {
      cancelAnimationFrame(this.raf);
      this.driving = false;
      this.classList.remove('is-driving');
      this.messages.forEach((message, i) => {
        message.classList.toggle('is-current', i === index);
        message.classList.remove('is-incoming');
        if (i === index) {
          message.removeAttribute('aria-hidden');
        } else {
          message.setAttribute('aria-hidden', 'true');
        }
      });
      this.index = index;
    }
  }

  customElements.define('van-announcement-bar', VanAnnouncementBar);
})();
